/**
 * Vehicle Selector — 2-step: Car Brand → Wheel Size → tag-based search redirect
 * Brand→PCD mapping loaded from vehicle-data.json (asset).
 */

(function () {
  'use strict';

  let vehicleData = null;

  function getDataUrl() {
    const el = document.querySelector('[data-vehicle-data-url]');
    return el ? el.getAttribute('data-vehicle-data-url') : null;
  }

  /**
   * Brand list from Theme settings > Vehicle Selector, one "Brand:PCD" per line.
   * A malformed line is skipped rather than breaking the whole list.
   */
  function readBrandsFromSettings() {
    const el = document.querySelector('script[data-vehicle-brands]');
    if (!el) return null;
    let raw;
    try {
      raw = JSON.parse(el.textContent);
    } catch (e) {
      raw = el.textContent;
    }
    if (!raw || typeof raw !== 'string') return null;

    const data = {};
    raw.split('\n').forEach(function (line) {
      const parts = line.split(':');
      if (parts.length < 2) return;
      const brand = parts[0].trim();
      // Trailing ":off" keeps a brand listed but greyed out, same as
      // "available": false in vehicle-data.json.
      let off = false;
      if (parts.length > 2 && /^(off|false|0)$/i.test(parts[parts.length - 1].trim())) {
        off = true;
        parts.pop();
      }
      const pcd = parts.slice(1).join(':').trim();
      if (!brand || !pcd) return;
      data[brand] = off ? { pcd: pcd, available: false } : { pcd: pcd };
    });
    return Object.keys(data).length ? data : null;
  }

  async function fetchJsonData() {
    const url = getDataUrl();
    if (!url) return null;
    const res = await fetch(url);
    if (!res.ok) return null;
    return res.json();
  }

  /*
    Two sources, one shape. The settings textarea owns which brands appear and
    their PCD, because the client edits that without touching code. Only the
    JSON file carries the size list per brand, so it is merged in rather than
    skipped: editing the textarea must not silently switch size filtering off.
  */
  async function loadVehicleData() {
    if (vehicleData) return vehicleData;

    let json = null;
    try {
      json = await fetchJsonData();
    } catch (e) {
      console.error('[VehicleSelector]', e);
    }

    const fromSettings = readBrandsFromSettings();
    if (fromSettings) {
      if (json) {
        Object.keys(fromSettings).forEach(function (brand) {
          const sizes = json[brand] && json[brand].sizes;
          if (sizes) fromSettings[brand].sizes = sizes;
        });
      }
      vehicleData = fromSettings;
      return vehicleData;
    }

    vehicleData = json;
    return vehicleData;
  }

  function setError(select, hasError) {
    if (hasError) {
      select.classList.add('vs-select--error');
    } else {
      select.classList.remove('vs-select--error');
    }
  }

  function initCard(card) {
    const brandSelect = card.querySelector('.vs-select.vs-brand');
    const sizeSelect  = card.querySelector('.vs-select.vs-size');
    const searchBtn   = card.querySelector('.vs-search-btn');

    if (!brandSelect || !sizeSelect || !searchBtn) return;

    // Populate brands on load
    loadVehicleData().then(function (data) {
      if (!data) return;
      const brands = Object.keys(data).filter(function (b) {
        return b.charAt(0) !== '_';
      }).sort();
      const currentVal = brandSelect.value;
      brandSelect.innerHTML = '<option value="">Select Car Brand</option>';
      brands.forEach(function (brand) {
        const opt = document.createElement('option');
        opt.value = brand;
        // The client wants every brand listed even where nothing fits yet,
        // so the ones with no wheels stay visible but cannot be chosen.
        if (data[brand].available === false) {
          opt.disabled = true;
          opt.textContent = brand + ' (coming soon)';
        } else {
          opt.textContent = brand;
          if (brand === currentVal) opt.selected = true;
        }
        brandSelect.appendChild(opt);
      });
      if (brandSelect.value) syncSizes(brandSelect.value);
    });

    /*
      A shopper picking Audi then 16" hit "No products found", because the size
      list never changed. Each brand carries the sizes the catalogue actually
      has for it, so the ones with nothing stay listed but cannot be chosen.
      Brands loaded from the settings textarea carry no sizes, and there every
      size stays selectable rather than being wrongly greyed out.
    */
    var allSizes = Array.prototype.map.call(sizeSelect.options, function (o) {
      return { value: o.value, label: o.textContent.trim() };
    });

    function syncSizes(brand) {
      loadVehicleData().then(function (data) {
        var sizes = data && data[brand] && data[brand].sizes;
        var chosen = sizeSelect.value;
        var stillThere = false;

        allSizes.forEach(function (item, i) {
          var opt = sizeSelect.options[i];
          if (!opt || !item.value) return;
          var has = !sizes || sizes.indexOf(item.value) !== -1;
          opt.disabled = !has;
          opt.textContent = has ? item.label : item.label + ' (none for ' + brand + ')';
          if (has && item.value === chosen) stillThere = true;
        });

        if (chosen && !stillThere) {
          sizeSelect.value = '';
          setError(sizeSelect, false);
        }
      });
    }

    function resetSizes() {
      allSizes.forEach(function (item, i) {
        var opt = sizeSelect.options[i];
        if (!opt) return;
        opt.disabled = false;
        opt.textContent = item.label;
      });
    }

    // Clear error on change
    brandSelect.addEventListener('change', function () {
      setError(brandSelect, false);
      if (brandSelect.value) syncSizes(brandSelect.value);
      else resetSizes();
    });
    sizeSelect.addEventListener('change', function () {
      setError(sizeSelect, false);
    });

    // Search button
    searchBtn.addEventListener('click', function () {
      const brand = brandSelect.value;
      const size  = sizeSelect.value;
      let valid = true;

      if (!brand) { setError(brandSelect, true); valid = false; }
      if (!size)  { setError(sizeSelect, true);  valid = false; }
      if (!valid) return;

      loadVehicleData().then(function (data) {
        if (!data || !data[brand] || data[brand].available === false) {
          setError(brandSelect, true);
          return;
        }

        /*
          Both filters read metafields set up in Search & Discovery: custom.compatiablevehicle
          holds the brands the client listed in their fitment column, custom.wheelsize
          holds 15" .. 23". q=* is Shopify's match-all, needed because /search returns
          nothing on an empty query even when filters are present.

          Brand used to be matched by searching the PCD as free text. That guessed one
          PCD per brand, so Skoda returned 72 wheels where the client lists 2, and
          Renault returned 2 where the client lists 24.
        */
        const url = '/search?type=product&q=*'
          + '&filter.p.m.custom.compatiablevehicle=' + encodeURIComponent(brand)
          + '&filter.p.m.custom.wheelsize=' + encodeURIComponent(size)
          + '&vehicle=' + encodeURIComponent(brand);

        window.location.href = url;
      });
    });

    // Enter key in either select triggers search
    [brandSelect, sizeSelect].forEach(function (sel) {
      sel.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') searchBtn.click();
      });
    });
  }

  function initAll() {
    document.querySelectorAll('.vehicle-selector-card').forEach(initCard);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) {
      e.target.querySelectorAll('.vehicle-selector-card').forEach(initCard);
    }
  });

})();
